import {t} from "@lingui/core/macro";
import gql from "graphql-tag";
import * as React from "react";

import {ModalLauncher} from "@khanacademy/wonder-blocks-modal";
import type {ApolloGqlClient} from "@khan/data-gql-apollo";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import Log, {Errors, KAError} from "@khan/logging";
import {PermissionsInfo} from "@khan/shared-core";

import type {
    ProfileAccessLevel,
    ProfileModelType,
} from "#shared-package/profile-model.ts";

import type {
    updateProfile,
    updateProfileVariables,
} from "./__graphql-types__/updateProfile.ts";
import ApolloQuery from "./gql/apollo-query.tsx";
import {getUnsafeApolloGqlClient} from "./gql/get-unsafe-apollo-gql-client.ts";
import ErrorModal from "./ui/error-modal.tsx";
import {
    fetchProfileData,
    profileFromQueryData,
    userProfileQuery,
    type ProfileData,
} from "./user-profile-query.ts";

type RenderProps = {
    // Set to true when SSR-ing or when waiting for profile data on
    // the client.
    loading: boolean;
    // If an error is encountered when requesting profile data from
    // the server this will contain the error.
    error?: Error;
    // The user profile.  Will be set when `loading` is `false` and
    // error is undefined.
    profile: ProfileModelType | null;
};

/**
 * UserProfileContext
 *
 * This React Context object can be used to override parts or all of the user profile
 * object provided by UserProfileProvider.  This is useful both in unit tests and in
 * fixtures.
 */
export const UserProfileContext: React.Context<RenderProps | null> =
    React.createContext<RenderProps | null>(null);
UserProfileContext.displayName = "UserProfileContext";

export type UserProfileProviderRenderProps = RenderProps;

type ActionResultsProfile = {
    countVideosCompleted: number;
    points: number;
    countBrandNewNotifications: number;
};

/**
 * Update the user profile in the store from Action Results.
 *
 * This function accepts an object of attributes to update.  It will
 * create a new ProfileModel based on the existing profile in the store
 * or create a new one if there isn't one.
 *
 * WARNING: Do not pass in a ProfileModel.
 */

export const updateProfileWithActionResults = (
    actionResults: ActionResultsProfile,
) => {
    const curUserId = userId;
    if (!curUserId) {
        return;
    }

    // TODO(somewhatabstract): Really should have the client passed into this
    // function by callers.
    // eslint-disable-next-line no-restricted-syntax
    const client = getUnsafeApolloGqlClient();
    client.cache().writeData({
        id: `User:${curUserId}`,
        // sync-start:profile-model-mapping 1015737946 services/static/apps/khanacademy/src/data-access/user-profile-query.ts
        data: {
            countVideosCompleted: actionResults.countVideosCompleted,
            points: actionResults.points,
            newNotificationCount: actionResults.countBrandNewNotifications,
        },
        // sync-end:profile-model-mapping
    });
};

export const UPDATE_PROFILE_MUTATION: GqlOperation<
    updateProfile,
    updateProfileVariables
> = gqlOp(gql`
    mutation updateProfile(
        $avatarName: String
        $bio: String
        $profileAccessLevel: ProfileAccessLevel
        $nickname: String
        $username: String
    ) {
        setSettings(
            avatarName: $avatarName
            bio: $bio
            nickname: $nickname
            username: $username
            profileAccessLevel: $profileAccessLevel
        ) {
            user {
                id
                avatar {
                    name
                    imageSrc
                }
                bio
                profile {
                    accessLevel
                }
                nickname
                username
            }
            errors {
                code
            }
        }
    }
`);

type Asset = {
    name: string;
    imageSrc: string;
    __typename?: string;
};

export type UpdatableProfileData = {
    avatar?: Asset;
    bio?: string;
    profile?: {
        accessLevel: ProfileAccessLevel;
        __typename?: string;
    };
    nickname?: string;
    username?: string;
    newNotificationCount?: number;
};

export function eagerUpdateProfileCache(
    gqlClient: ApolloGqlClient,
    data: UpdatableProfileData,
) {
    const curUserId = userId;
    if (!curUserId) {
        return;
    }

    const updates = {...data} as const;
    if (data.avatar) {
        // @ts-expect-error - TS2540 - Cannot assign to 'avatar' because it is a read-only property.
        updates.avatar = {
            ...data.avatar,
            __typename: "Avatar",
        };
    }

    if (data.profile) {
        // @ts-expect-error - TS2540 - Cannot assign to 'profile' because it is a read-only property.
        updates.profile = {
            ...data.profile,
            __typename: "Profile",
        };
    }

    gqlClient.cache().writeData({
        id: `User:${curUserId}`,
        data: updates,
    });
}

/**
 * saveAndUpdateProfile will handle the saving, persisting, and Apollo
 * cache updates of the editable profile fields.  In order to keep an eager
 * UI, we will update the apollo cache before mutating.  If anything fails,
 * the apollo cache will update again based on the mutation result
 *
 * @param data UpdatableProfileData with the following possible fields:
 *   - avatar: {name: string, imageSrc: string}
 *   - bio: string
 *   - profileAccessLevel: ProfileAccessLevel
 *   - nickname: string
 *   - username: string
 *
 * @returns {Promise<UpdatableProfileData>} Callers are responsible for
 * catching errors
 */
export function saveAndUpdateProfile(
    gqlClient: ApolloGqlClient,
    data: UpdatableProfileData,
): Promise<UpdatableProfileData> {
    eagerUpdateProfileCache(gqlClient, data);

    const variables = {
        avatarName: data.avatar?.name,
        bio: data.bio,
        profileAccessLevel: data.profile?.accessLevel,
        nickname: data.nickname,
        username: data.username,
    } as const;

    // @ts-expect-error - TS2322 - Type 'Promise<UpdatableProfileData | { avatar: { name: string; imageSrc: string; }; bio: string; profile: { accessLevel: string; }; nickname: string; username: string; }>' is not assignable to type 'Promise<UpdatableProfileData>'.
    return gqlClient
        .mutate(UPDATE_PROFILE_MUTATION, {
            variables,
        })
        .then((response) => {
            // This should not happen, but in unexpected error states, it
            // might happen.
            if (!response.data?.setSettings?.user) {
                throw new KAError(
                    "Did not receive a proper response from the query",
                    Errors.Internal,
                );
            }

            const updatedUser = response.data.setSettings.user;
            const {avatar, bio, profile, nickname, username} = updatedUser;

            // Always default to COACH
            let accessLevel = "COACH";
            if (profile && profile.accessLevel) {
                accessLevel = profile.accessLevel;
            }

            return {
                avatar: {
                    name: avatar.name,
                    imageSrc: avatar.imageSrc,
                },
                bio: bio ?? "",
                profile: {
                    accessLevel,
                },
                nickname: nickname ?? "",
                username: username ?? "",
            };
        });
}

/**
 * Update the user profile in the store.
 *
 * This function re-fetches the user profile data from GraphQL and updates
 * the cache with the latest data, but only if that data hasn't changed.
 */
export const refetchAndUpdateProfile = (
    gqlClient: ApolloGqlClient,
): Promise<ProfileData | null | undefined> => {
    const curUserId = userId;
    if (!curUserId) {
        // @ts-expect-error - TS2322 - Type 'Promise<void>' is not assignable to type 'Promise<{ actorIsImpersonatingUser: boolean | null | undefined; hasAccessToAIGuideDev: boolean; isAIGuideEnabled: boolean; user: getFullUserProfile_user | null | undefined; } | null | undefined>'.
        return Promise.resolve();
    }

    // Read the existing user data out of the query cache. We use this to make
    // sure that we don't write out new data if the profile hasn't changed
    // (we want to reduce the number of re-renders, if possible).
    const oldUser = gqlClient
        .cache()
        .readQuery({query: userProfileQuery})?.user;

    // @ts-expect-error - TS2322 - Type 'Promise<void | { actorIsImpersonatingUser: boolean | null | undefined; hasAccessToAIGuideDev: boolean; isAIGuideEnabled: boolean; user: getFullUserProfile_user | null | undefined; } | null | undefined>' is not assignable to type 'Promise<{ actorIsImpersonatingUser: boolean | null | undefined; hasAccessToAIGuideDev: boolean; isAIGuideEnabled: boolean; user: getFullUserProfile_user | null | undefined; } | null | undefined>'.
    return fetchProfileData(gqlClient, {}).then(({user}: ProfileData) => {
        // This is a kludgy way of determining if these two objects are the
        // same, but it works!
        if (JSON.stringify(user) === JSON.stringify(oldUser)) {
            return;
        }

        gqlClient.cache().writeData({
            id: `User:${curUserId}`,
            data: user,
        });
    });
};

type Props = Readonly<{
    children: (props: RenderProps) => React.ReactNode;
}>;

type State = {
    profile: ProfileModelType | null;
};

let userId: string | null | undefined = null;
export const setUserIdForTesting = (id?: string | null): void => {
    userId = id;
};

// This is only exported for the purposes of testing.
export const _errorState = {
    reported: false,
} as const;

export const _promiseCallbacks = {
    resolve: (profile: ProfileModelType | null) => {},
    reject: () => {},
} as const;

/**
 * Provides access to user profile data in an SSR-safe way.
 *
 * Example usage:
 * render(): React.Node {
 *   return <UserProfileProvider>
 *     {({loading, error, profile}) => {
 *       if (loading) {
 *         return <Spinner />;
 *       } else if (error) {
 *         return <ErrorMessage />;
 *       } else if (profile) {
 *         return ... // markup using data from profile
 *       } else {
 *         throw new KAError("This should never happen", Errors.Internal);
 *       }
 *     }}
 *   </UserProfileProvider>;
 * }
 *
 * This component takes a render prop for children that contains
 * thre props: `loading`, `error`, and `profile`.  If `loading` is
 * true, the other two props will be undefined.  If `loading` is
 * false, either `error` or `profile` will be defined.
 */
export default class UserProfileProvider extends React.Component<Props, State> {
    _profile: ProfileModelType | null;
    _profileData: ProfileData | null | undefined;
    _showErrorModal: boolean;

    static resolve: (arg1: ProfileModelType | null) => void;

    constructor(props: Props) {
        super(props);
        this._profile = null;
        this._showErrorModal = false;
    }
    renderQuery(): React.ReactNode {
        const {children} = this.props;

        return (
            <ApolloQuery query={userProfileQuery} fetchPolicy="cache-first">
                {(result) => {
                    const {loading, data, error} = result;

                    if (loading) {
                        return children({
                            loading,
                            profile: null,
                        });
                    }

                    if (error) {
                        return children({
                            loading,
                            error,
                            profile: null,
                        });
                    }

                    const user = data && data.user;

                    if (!user) {
                        if (!_errorState.reported) {
                            // Log an error.
                            Log.error(
                                "No user data from GraphQL query for current user",
                                Errors.Internal,
                            );
                            // Only report this error once per page load.
                            // @ts-expect-error - TS2540 - Cannot assign to 'reported' because it is a read-only property.
                            _errorState.reported = true;

                            // Only have this one instance of UserProfileProvider
                            // render an error modal.
                            this._showErrorModal = true;
                        }

                        if (this._showErrorModal) {
                            const modal = (
                                <ErrorModal
                                    title={t`Something went wrong.`}
                                    message={t`Oops! Looks like we are having trouble loading data about your user account.`}
                                />
                            );

                            return (
                                <React.Fragment>
                                    <ModalLauncher
                                        modal={modal}
                                        opened={true}
                                        // The modal forces a choice to either reload or logout
                                        onClose={() => {}}
                                    />
                                    {children({
                                        loading: result.loading,
                                        error: new KAError(
                                            "Bad data from GraphQL query for user profile.",
                                            Errors.Internal,
                                        ),
                                        profile: null,
                                    })}
                                </React.Fragment>
                            );
                        }

                        return children({
                            loading: result.loading,
                            error: new KAError(
                                "Bad data from GraphQL query for user profile.",
                                Errors.Internal,
                            ),
                            profile: null,
                        });
                    }

                    if (!userId) {
                        userId = user.id ?? null;
                    }

                    // @ts-expect-error - TS2367 - This condition will always return 'true' since the types 'getFullUserProfile_user' and '{ actorIsImpersonatingUser: boolean | null | undefined; hasAccessToAIGuideDev: boolean; isAIGuideEnabled: boolean; user: getFullUserProfile_user | null | undefined; } | null | undefined' have no overlap.
                    if (data && user !== this._profileData) {
                        this._profile = profileFromQueryData(data);
                        this._profileData = data;
                    }

                    _promiseCallbacks.resolve(this._profile);

                    return children({
                        loading: false,
                        profile: this._profile,
                    });
                }}
            </ApolloQuery>
        );
    }

    render(): React.ReactNode {
        const {children} = this.props;

        return (
            <UserProfileContext.Consumer>
                {(mock: RenderProps | null) => {
                    // If the value in UserProfileContext is defined
                    // use that and by-pass the store and querying for
                    // the date.  This should only be used for testing.
                    if (mock) {
                        return children({
                            ...mock,
                            profile: mock.profile,
                        });
                    }

                    // If we know the user doesn't have a profile, i.e.
                    // they aren't logged in and aren't a phantom user,
                    // we can skip querying for the user profile.
                    if (!PermissionsInfo.hasProfile) {
                        return children({
                            loading: false,
                            profile: null,
                        });
                    }

                    // Get the user's profile for real.
                    return this.renderQuery();
                }}
            </UserProfileContext.Consumer>
        );
    }
}

const _profilePromise = new Promise<ProfileModelType | null>(
    (resolve: unknown, reject: unknown) => {
        // @ts-expect-error - TS2540 - Cannot assign to 'resolve' because it is a read-only property.
        _promiseCallbacks.resolve = resolve;
        // @ts-expect-error - TS2540 - Cannot assign to 'reject' because it is a read-only property.
        _promiseCallbacks.reject = reject;
    },
);

export const getProfilePromise = ():
    | Promise<null>
    | Promise<ProfileModelType | null> => {
    return PermissionsInfo.hasProfile ? _profilePromise : Promise.resolve(null);
};
